import { useApolloClient } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { PrivacyToggleProvider } from '@eluve/blocks';
import {
  Icon,
  Layout,
  Main,
  Sidebar,
  SidebarContent,
  SidebarContentSpacer,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarLogoHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuLinkButton,
  SidebarMenuNavButton,
  useSidebar,
} from '@eluve/components';
import { EluveGptTrigger } from '@eluve/feature-chat-with-patient-data';
import { useTranslation } from '@eluve/i18n';
import { useNamedLogger } from '@eluve/logger';
import {
  MedplumFhirRendererProvider,
  MedplumProvider,
} from '@eluve/medplum-client';
import { usePostHog } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import {
  FeatureFlaggedComponent,
  GlobalStatusWidget,
  TenantOrUserFeatureFlaggedComponent,
  UserFeatureFlaggedComponent,
  useTenant,
} from '@eluve/smart-blocks';
import { useAppointmentRecordingStatusStoreApi } from '@eluve/user-local-files';
import { SUPPORT_URL } from '@eluve/utils';

import { showIntercom, useIntercomStatus } from '../../../../IntercomProvider';
import { appConfig } from '../../../../config';
import { TenantUserSettings } from '../../TenantUserSettings';
import { useIsCarePlanFeatureEnabled } from '../CarePlanFeatureGate';
import { UserDropdownMenu } from '../components/UserDropdownMenu';

import { ImpersonationExpiryMonitor } from './ImpersonationExpiryMonitor';
import { TranscriptionWidget } from './appointments/machines/transcription/components/TranscriptionWidget';
import { useTranscriptionMachineSelector } from './appointments/machines/transcription/hooks/useTranscriptionMachine';

const IntercomSupportButton = () => {
  const { overlayOpen, setOverlayOpen } = useSidebar();
  const { isIntercomReady } = useIntercomStatus();
  const { t } = useTranslation();

  if (isIntercomReady) {
    return (
      <SidebarMenuButton
        hideIconWhenExpanded
        onClick={() => {
          showIntercom();
          if (overlayOpen) {
            setOverlayOpen(false);
          }
        }}
        tooltip={t('sidebar.support')}
      >
        <Icon name="CircleHelp" size="auto" />
        <span>{t('sidebar.support')}</span>
      </SidebarMenuButton>
    );
  }

  return (
    <SidebarMenuLinkButton
      hideIconWhenExpanded
      href={SUPPORT_URL}
      tooltip={t('sidebar.support')}
    >
      <Icon name="CircleHelp" size="auto" />
      <span>{t('sidebar.support')}</span>
    </SidebarMenuLinkButton>
  );
};

// Rendered inside <TenantUserSettings> so useTranslation reads the
// settings-driven I18nProvider language (TenantLayout itself sits above it).
const TenantSidebar: React.FC<{
  tenantId: string;
  isRecording: boolean;
  isCarePlanFeatureEnabled: boolean;
  onLogoClick: () => void;
}> = ({ tenantId, isRecording, isCarePlanFeatureEnabled, onLogoClick }) => {
  const { t } = useTranslation();

  return (
    <Sidebar variant="inset">
      <SidebarHeader>
        <SidebarLogoHeader onClick={onLogoClick} />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuNavButton
                  size="lg"
                  to={`/organizations/${tenantId}/home`}
                  tooltip={t('sidebar.overview')}
                >
                  <Icon name="Calendar" size="auto" />
                  <span>{t('sidebar.overview')}</span>
                </SidebarMenuNavButton>
              </SidebarMenuItem>
              <FeatureFlaggedComponent flag="APPOINTMENTS_VIEW">
                <SidebarMenuItem>
                  <SidebarMenuNavButton
                    size="lg"
                    to={`/organizations/${tenantId}/appointments`}
                    tooltip={t('sidebar.appointments')}
                  >
                    <Icon name="FileEdit" size="auto" />
                    <span>{t('sidebar.appointments')}</span>
                  </SidebarMenuNavButton>
                </SidebarMenuItem>
              </FeatureFlaggedComponent>
              <FeatureFlaggedComponent flag="PATIENTS_VIEW">
                <SidebarMenuItem>
                  <SidebarMenuNavButton
                    size="lg"
                    to={`/organizations/${tenantId}/patients`}
                    tooltip={t('sidebar.patients')}
                  >
                    <Icon name="User" size="auto" />
                    <span>{t('sidebar.patients')}</span>
                  </SidebarMenuNavButton>
                </SidebarMenuItem>
              </FeatureFlaggedComponent>
              <FeatureFlaggedComponent flag="MEDPLUM">
                <SidebarMenuItem>
                  <SidebarMenuNavButton
                    size="lg"
                    to={`/organizations/${tenantId}/services`}
                    tooltip={t('sidebar.services')}
                  >
                    <Icon name="Stethoscope" size="auto" />
                    <span>{t('sidebar.services')}</span>
                  </SidebarMenuNavButton>
                </SidebarMenuItem>
              </FeatureFlaggedComponent>
              <FeatureFlaggedComponent flag="CLINICAL_TASKS">
                <UserFeatureFlaggedComponent flag="CLINICAL_TASKS">
                  <SidebarMenuItem>
                    <SidebarMenuNavButton
                      size="lg"
                      to={`/organizations/${tenantId}/tasks`}
                      tooltip={t('sidebar.tasks')}
                    >
                      <Icon name="ListChecks" size="auto" />
                      <span>{t('sidebar.tasks')}</span>
                    </SidebarMenuNavButton>
                  </SidebarMenuItem>
                </UserFeatureFlaggedComponent>
              </FeatureFlaggedComponent>
              {isCarePlanFeatureEnabled && (
                <SidebarMenuItem>
                  <SidebarMenuNavButton
                    size="lg"
                    to={`/organizations/${tenantId}/care-library`}
                    tooltip={t('sidebar.careLibrary')}
                  >
                    <Icon name="Layers" size="auto" />
                    <span>{t('sidebar.careLibrary')}</span>
                  </SidebarMenuNavButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarContentSpacer />
        <SidebarGroup>
          <SidebarGroupContent>
            <GlobalStatusWidget isRecording={isRecording} />
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuNavButton
                  hideIconWhenExpanded
                  to={`/organizations/${tenantId}/settings`}
                  tooltip={t('sidebar.settings')}
                >
                  <Icon name="Settings" size="auto" />
                  <span>{t('sidebar.settings')}</span>
                </SidebarMenuNavButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <IntercomSupportButton />
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <UserDropdownMenu />
      </SidebarFooter>
    </Sidebar>
  );
};

export const TenantLayout = () => {
  const client = useApolloClient();
  const logger = useNamedLogger('TenantLayout');
  const navigate = useNavigate();
  const postHog = usePostHog();
  const isCarePlanFeatureEnabled = useIsCarePlanFeatureEnabled();
  const tenantId = useAssignedTenantIdFromParams();
  const tenant = useTenant(tenantId);
  const userId = useUserIdFromSession();

  const isIdleOrPaused = useTranscriptionMachineSelector(
    (state) => state.matches('Idle') || state.matches({ InSession: 'Paused' }),
  );
  const isRecording = useTranscriptionMachineSelector((state) =>
    state.matches({ InSession: 'Recording' }),
  );

  const appointmentRecordingStatusStore =
    useAppointmentRecordingStatusStoreApi();

  useEffect(
    function syncAppointmentRecordingStatus() {
      appointmentRecordingStatusStore.setState({
        isActivelyRecording: !isIdleOrPaused,
      });
    },
    [isIdleOrPaused, appointmentRecordingStatusStore],
  );

  const localStorageKey = `last-tenant-id-for-user-${userId}`;

  const lastTenantId = useMemo(
    () => localStorage.getItem(localStorageKey),
    [localStorageKey],
  );

  useEffect(
    function trackLastAccessedTenantId() {
      if (tenantId !== lastTenantId) {
        localStorage.setItem(localStorageKey, tenantId);
      }
    },
    [tenantId, lastTenantId, client, logger, localStorageKey],
  );

  useEffect(() => {
    if (!tenant) return;
    postHog.group('tenant', tenantId, {
      name: tenant.name,
      createdAt: tenant.createdAt,
      isRealTenant: tenant.isRealTenant,
    });
  }, [
    postHog,
    tenantId,
    tenant?.name,
    tenant?.createdAt,
    tenant?.isRealTenant,
  ]);

  const handleLogoClick = () => {
    navigate(`/organizations/${tenantId}/home`);
  };

  return (
    <TenantUserSettings>
      <Layout>
        <TenantSidebar
          tenantId={tenantId}
          isRecording={isRecording}
          isCarePlanFeatureEnabled={isCarePlanFeatureEnabled}
          onLogoClick={handleLogoClick}
        />
        <Main>
          <PrivacyToggleProvider>
            <ImpersonationExpiryMonitor />
            <MedplumProvider baseUrl={appConfig.VITE_MEDPLUM_BASE_URL}>
              <MedplumFhirRendererProvider>
                <Outlet />
              </MedplumFhirRendererProvider>
            </MedplumProvider>
            {/* Rendered here (fixed-position, so placement is unaffected) so the
              chat inherits the settings-driven I18nProvider language. */}
            <TenantOrUserFeatureFlaggedComponent flag="ELUVE_GPT">
              <EluveGptTrigger
                tenantId={tenantId}
                baseUrl={appConfig.VITE_API_DOMAIN}
              />
            </TenantOrUserFeatureFlaggedComponent>
          </PrivacyToggleProvider>
        </Main>
        <TranscriptionWidget />
      </Layout>
    </TenantUserSettings>
  );
};
